import { ReactNode, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material'
import { Redemption } from '../../api/types'
import { useOpsApi } from '../../api/context'
import { saveBlob } from '../../api/download'
import { apiError } from '../../api/errors'
import { InfoHint } from '../../components/InfoHint'
import { StatusChip } from '../../components/StatusChip'
import { FeeChecksPanel } from '../fee/FeeChecksPanel'
import { RedemptionTranchesTable } from './RedemptionTranchesTable'
import { formatCzk, formatDate, formatDateTime, formatRate, formatUnits, formatUnits4 } from './format'

function Row({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <>
      <Typography variant="caption" color="text.secondary" sx={{ pt: '2px' }}>
        {label} {hint}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  )
}

interface RedemptionDetailDialogProps {
  open: boolean
  redemption: Redemption | null
  /** Právě běží akce (potvrzení / výplata / storno) — blokuje tlačítka. */
  submitting: boolean
  isCeo: boolean
  isBackoffice: boolean
  /** Potvrzení žádosti (NEW → QUEUED). Bez handleru se tlačítko nezobrazí. */
  onAcceptClick?: () => void
  /** Potvrzení odkupu CEO (REQUESTED → CONFIRMED). Bez handleru se tlačítko nezobrazí. */
  onConfirmClick?: () => void
  onPayoutClick?: () => void
  onCancelClick?: () => void
  onClose: () => void
}

/** Stavy, ve kterých jde žádost ještě stornovat (v core se nic nestalo). */
export const CANCELLABLE_REDEMPTION: Redemption['status'][] = ['NEW', 'QUEUED', 'REQUESTED']

/**
 * Detail odkupu: čísla výplaty, držba před/po, rozpad výkonnostní odměny per
 * tranše, kontroly (invarianty + AI), PDF vyúčtování a akce. POTVRDIT a VYPLACENO
 * smí jen CEO (odkup sahá do core na reálné peníze), potvrzení žádosti a STORNO
 * backoffice a jen u nepotvrzené žádosti; ostatní vidí detail read-only. Které
 * akce dialog nabízí, určuje volající předanými handlery.
 */
export function RedemptionDetailDialog({
  open,
  redemption,
  submitting,
  isCeo,
  isBackoffice,
  onAcceptClick,
  onConfirmClick,
  onPayoutClick,
  onCancelClick,
  onClose,
}: RedemptionDetailDialogProps) {
  const api = useOpsApi()
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  // Chyba stažení patří jen k právě otevřenému odkupu.
  useEffect(() => {
    setDownloadError(null)
  }, [redemption?.id, open])

  if (!redemption) {
    return <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth />
  }

  const r = redemption
  const isNew = r.status === 'NEW'
  const isRequested = r.status === 'REQUESTED'
  const isConfirmed = r.status === 'CONFIRMED'
  const isCancellable = CANCELLABLE_REDEMPTION.includes(r.status)
  // Nová žádost a žádost ve frontě ještě nemají cenu (ocení se k Dni ocenění).
  const priced = Boolean(r.unitPrice)
  const executed = isConfirmed || r.status === 'PAID_OUT'
  const holding = r.holdingUnits
  // `holdingUnits` je AKTUÁLNÍ držba: u žádosti ještě před odkupem, po potvrzení už snížená.
  const holdingBefore = holding === undefined ? undefined : executed ? holding + r.units : holding
  const holdingAfter = holdingBefore === undefined ? undefined : holdingBefore - r.units

  const handleStatement = async () => {
    setDownloading(true)
    setDownloadError(null)
    try {
      saveBlob(await api.redemptionStatement(r.id), `vyuctovani-odkupu-${r.id}.pdf`)
    } catch (err) {
      setDownloadError(apiError(err, 'Vyúčtování se nepodařilo stáhnout'))
    } finally {
      setDownloading(false)
    }
  }

  const grid = { m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 3, rowGap: 1 }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <span>
          Odkup {r.id} — {r.clientName}
        </span>
        <StatusChip status={r.status} />
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Section title="Odkup">
            <Box component="dl" sx={grid}>
              <Row label="Fond / třída" value={`${r.fundKey} · ${r.feeClass === 'A' ? 'A (EB)' : 'B (NORM)'}`} />
              <Row
                label="Ocenění"
                value={
                  r.valuationDate
                    ? `${formatDate(r.valuationDate)}${r.valuationId ? ` (č. ${r.valuationId})` : ''}`
                    : '—'
                }
                hint={
                  <InfoHint
                    lines={[
                      'Odkup se oceňuje cenou jednotky z vybraného ocenění; prázdné zadání použije poslední ruční ocenění fondu.',
                      'Stejná cena, jakou by klient dostal při přidělení jednotek — jeden fond = jedna cena.',
                    ]}
                  />
                }
              />
              <Row label="Cena jednotky" value={formatUnits(r.unitPrice)} />
              <Row
                label="Zadáno"
                value={
                  r.requestedUnits !== undefined && r.requestedUnits !== null
                    ? `${formatUnits4(r.requestedUnits)} jednotek`
                    : r.requestedAmount !== undefined && r.requestedAmount !== null
                    ? `${formatCzk(r.requestedAmount)} (částkou)`
                    : '—'
                }
                hint={
                  <InfoHint
                    lines={[
                      'Zadává se BUĎ počet jednotek, NEBO částka v Kč — druhé dopočte server cenou ocenění.',
                      'Zadaná částka je hrubá hodnota před odměnou a poplatkem; čistá výplata je nižší.',
                    ]}
                  />
                }
              />
              <Row label="Odkupované jednotky" value={formatUnits4(r.units)} />
              <Row
                label="Držba (před → po)"
                value={
                  holdingBefore === undefined
                    ? '—'
                    : `${formatUnits4(holdingBefore)} → ${formatUnits4(holdingAfter ?? 0)}`
                }
                hint={
                  <InfoHint
                    lines={[
                      'Držba klienta ve fondu před odkupem a po něm — kontrola, že odkup nepřesahuje, co klient drží.',
                      'Odměna ani poplatek se nestrhávají v jednotkách; klient prodá všechny odkupované jednotky a dostane peníze po srážkách.',
                    ]}
                  />
                }
              />
              {r.receivedDate && <Row label="Žádost doručena" value={formatDate(r.receivedDate)} />}
              {r.valuationDay && <Row label="Den ocenění" value={formatDate(r.valuationDay)} />}
              {r.payoutDue && <Row label="Splatnost výplaty" value={formatDate(r.payoutDue)} />}
              {r.queueError && (
                <Row
                  label="Proč zatím není oceněno"
                  value={
                    <Box component="span" sx={{ color: 'error.main' }}>
                      {r.queueError}
                    </Box>
                  }
                />
              )}
              <Row label="Číslo účtu" value={r.accountNumber || '—'} />
              {r.note && <Row label="Poznámka" value={r.note} />}
            </Box>
          </Section>

          <Divider />

          {priced ? (
            <>
              <Section title="Výplata">
                <Box component="dl" sx={grid}>
                  <Row
                    label="Hrubá hodnota"
                    value={formatCzk(r.grossAmount)}
                    hint={<InfoHint lines={['Odkupované jednotky × cena jednotky.']} />}
                  />
                  <Row
                    label={`Výkonnostní odměna (${formatRate(r.sfRate)})`}
                    value={
                      r.sfUnits
                        ? `− ${formatCzk(r.sfFee)} (${formatUnits4(r.sfUnits)} j.)`
                        : `− ${formatCzk(r.sfFee)}`
                    }
                    hint={
                      <InfoHint
                        title="Success fee při odkupu"
                        lines={[
                          'Dle smlouvy se odměna zúčtovává i při odkupu — z odkupované části držby.',
                          'Stejná mechanika jako roční běh: tranše po vkladech, high-water mark, benchmark musí být překonán.',
                          `Benchmark ${formatRate(r.benchmarkPa ?? 0.07)} p.a. (Actual/360); pod hranicí je odměna z tranše 0.`,
                          'Sazba dle třídy: A/EB 15 %, B/NORM 20 % ze zisku nad výchozí hodnotou.',
                          'Odměna se strhává v jednotkách: ty přejdou na účet „success fee“, z fondu se odkoupí jen zbytek. Čistá výplata je stejná.',
                        ]}
                      />
                    }
                  />
                  <Row
                    label={`Výstupní poplatek (${formatRate(r.exitFeeRate)})`}
                    value={`− ${formatCzk(r.exitFee)}`}
                    hint={
                      <InfoHint
                        lines={[
                          'Poplatek z hrubé hodnoty odkupu; výchozí sazba fondu je 0 % a obsluha ji smí u konkrétní žádosti přepsat.',
                        ]}
                      />
                    }
                  />
                  <Row
                    label="Čistá výplata"
                    value={<strong>{formatCzk(r.netAmount)}</strong>}
                    hint={
                      <InfoHint lines={['Hrubá hodnota − výkonnostní odměna − výstupní poplatek.']} />
                    }
                  />
                  <Row
                    label="Distributor"
                    value={
                      r.distributorName
                        ? `${r.distributorName}${
                            r.sfCommissionRate !== undefined && r.sfCommissionRate !== null
                              ? ` · podíl ${formatRate(r.sfCommissionRate)}`
                              : ''
                          } · provize ${formatCzk(r.sfCommission ?? 0)}`
                        : '—'
                    }
                    hint={
                      <InfoHint
                        lines={[
                          'Provize distributora = výkonnostní odměna × jeho nasmlouvaný podíl ze SF (bez vyplněného podílu 0).',
                          'Provize nemění čistou výplatu klienta — dělí se z odměny, kterou si bere fond.',
                        ]}
                      />
                    }
                  />
                </Box>
              </Section>

              <Divider />

              <Section title="Rozpad výkonnostní odměny (per vklad)">
                <RedemptionTranchesTable
                  tranches={r.tranches ?? []}
                  totalFee={r.sfFee}
                  benchmarkPa={r.benchmarkPa}
                />
              </Section>
            </>
          ) : (
            <Alert severity="info">
              Výběr zatím není oceněný. Cena, odměna a čistá výplata se dopočtou k Dni ocenění.
            </Alert>
          )}

          {r.check && (
            <>
              <Divider />
              <Section title="Kontrola odkupu">
                <FeeChecksPanel check={r.check} aiTitle="AI kontrola (per klient)" />
              </Section>
            </>
          )}

          <Divider />

          <Section title="Historie">
            <Box component="dl" sx={grid}>
              <Row
                label="Založeno"
                value={`${formatDateTime(r.createdAt)}${r.createdBy ? ` · ${r.createdBy}` : ''}`}
              />
              {r.decidedAt && (
                <Row
                  label="Potvrzeno"
                  value={`${formatDateTime(r.decidedAt)}${r.decidedBy ? ` · ${r.decidedBy}` : ''}`}
                />
              )}
              {r.paidAt && (
                <Row
                  label="Vyplaceno"
                  value={`${formatDateTime(r.paidAt)}${r.paidBy ? ` · ${r.paidBy}` : ''}`}
                />
              )}
              {r.cancelledAt && <Row label="Stornováno" value={formatDateTime(r.cancelledAt)} />}
              <Row label="Rozpis odeslán klientovi" value={r.emailSent ? 'ano' : 'ne'} />
            </Box>
          </Section>

          {downloadError && <Alert severity="error">{downloadError}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {isCancellable && isBackoffice && onCancelClick && (
          <Button variant="outlined" color="error" onClick={onCancelClick} disabled={submitting}>
            Stornovat
          </Button>
        )}
        {priced && (
          <Button variant="text" onClick={handleStatement} disabled={submitting || downloading}>
            Vyúčtování PDF
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="text" color="inherit" onClick={onClose} disabled={submitting}>
          Zavřít
        </Button>
        {isNew && isBackoffice && onAcceptClick && (
          <Button variant="contained" onClick={onAcceptClick} disabled={submitting}>
            Potvrdit žádost
          </Button>
        )}
        {isRequested && isCeo && onConfirmClick && (
          <Button variant="contained" onClick={onConfirmClick} disabled={submitting}>
            Potvrdit
          </Button>
        )}
        {isConfirmed && isCeo && onPayoutClick && (
          <Button variant="contained" onClick={onPayoutClick} disabled={submitting}>
            Vyplaceno
          </Button>
        )}
        {isRequested && !isCeo && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Odkup potvrzuje CEO.
          </Typography>
        )}
      </DialogActions>
    </Dialog>
  )
}
